import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { PROJECTS, BRAND, MEDIA } from "../data/portfolio";
import { ParallaxImage, ScrollScale } from "../components/Motion";
import TemplateIntro from "../components/TemplateIntro";
import ChromeFace from "../components/ChromeFace";
import AurevonHero from "../components/AurevonHero";
import Buoy3D from "../components/Buoy3D";
import Seo from "../components/Seo";
import { caseStudySchema, SITE } from "../data/schema";

const INTRO_INITIAL = { opacity: 0, y: 30 };
const INTRO_ANIMATE = { opacity: 1, y: 0 };
const INTRO_TRANSITION = { duration: 0.8, ease: [0.22, 1, 0.36, 1] };

// Deck video with an intro label that fades out once playback begins.
function DeckVideo({ slide, i }) {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const near = useInView(ref, { once: true, margin: "600px 0px" });
  return (
    <div ref={ref} className="relative">
      <video
        key={near ? "loaded" : "idle"}
        poster={slide.poster}
        preload="none"
        autoPlay
        muted
        loop
        playsInline
        aria-label={slide.alt}
        onPlay={() => setPlaying(true)}
        className="w-full block rounded-[24px] border-[1.5pt] border-white/30"
        data-testid={`slide-video-${i}`}
      >
        {near && <source src={slide.url} type="video/mp4" />}
        {near && slide.webm && <source src={slide.webm} type="video/webm" />}
      </video>
      {slide.label && (
        <div
          className={`absolute inset-0 rounded-[24px] pointer-events-none transition-opacity duration-700 ${
            playing ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="absolute inset-0 rounded-[24px] bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
          <div className="absolute left-6 bottom-5 md:left-8 md:bottom-7">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1.5">Motion</p>
            <span className="font-display font-bold text-lg md:text-2xl tracking-tight text-white">{slide.label}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// True while the page is scrolled within 120px of the top.
function useAtTop() {
  const [atTop, setAtTop] = useState(true);
  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return atTop;
}

// Back-to-work button — visible at the top, hides on scroll, returns on hover.
function BackButton({ atTop }) {
  return (
    <div className="fixed left-4 md:left-8 top-24 z-[55]">
      <Link
        to="/#work"
        data-testid="case-back"
        className={`group/back inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 px-4 py-2.5 text-sm text-white transition-opacity duration-300 hover:opacity-100 focus-visible:opacity-100 ${
          atTop ? "opacity-100" : "opacity-0"
        }`}
      >
        <ArrowLeft size={16} className="transition-transform duration-300 group-hover/back:-translate-x-0.5" />
        All work
      </Link>
    </div>
  );
}

// Unsinkable-only hero layers: pink-water backdrop, desktop buoy panel and left scrim.
function ElevateBackdrop() {
  return (
    <>
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
        <img src={MEDIA.pinkWater} alt="Fuchsia ocean water backdrop from the Unsinkable keynote deck" className="w-full h-full object-cover scale-x-[-1]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]" />
      </div>
      <div className="hidden lg:block absolute top-0 right-0 h-full w-[64vw] z-[5]" data-testid="buoy-hero">
        <Buoy3D className="w-full h-full" />
      </div>
      <div
        className="hidden lg:block absolute inset-0 z-[8] pointer-events-none bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent"
        aria-hidden
      />
    </>
  );
}

function MetaGrid({ meta, className = "" }) {
  return (
    <div className={`grid grid-cols-2 gap-x-8 gap-y-8 ${className}`}>
      {meta.map((m) => (
        <div key={m.label}>
          <span className="text-white/40 block mb-2 uppercase tracking-[0.2em] text-[11px]">{m.label}</span>
          <span className="text-white/85 text-sm md:text-base">{m.value || "Not specified"}</span>
        </div>
      ))}
    </div>
  );
}

function CaseIntro({ project, meta }) {
  const isElevate = project.slug === "unsinkable-cinematic-keynote-deck";
  const heroVisual = project.heroVisual;
  const sideVisual = Boolean(heroVisual);
  return (
    <section className={`relative ${isElevate ? "overflow-hidden lg:min-h-[86vh] flex flex-col justify-center" : ""}`}>
      {project.heroBg && !sideVisual && (
        <div className="absolute inset-0 pointer-events-none">
          <ParallaxImage src={project.heroBg} alt={project.coverAlt} className="absolute inset-0" imgClassName="opacity-60" range={18} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-[#050505]/55 to-[#050505]" />
        </div>
      )}

      {isElevate && <ElevateBackdrop />}

      {sideVisual && (
        <div className={`absolute inset-y-0 right-0 ${heroVisual.boxWidth || "w-[48vw] max-w-[780px]"} flex items-center justify-end pr-2 md:pr-6 z-0 pointer-events-none`} data-testid="hero-visual-bg">
          <AurevonHero
            src={heroVisual.src}
            alt={heroVisual.alt}
            mask={heroVisual.mask !== false}
            maxH={heroVisual.maxH}
            className="h-full w-full"
          />
        </div>
      )}

      <div className={`relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 w-full pt-16 md:pt-20 ${isElevate ? "lg:pointer-events-none lg:[&_a]:pointer-events-auto" : ""}`}>
        <motion.div
          initial={INTRO_INITIAL}
          animate={INTRO_ANIMATE}
          transition={INTRO_TRANSITION}
          className="grid lg:grid-cols-12 gap-10 pb-16 md:pb-20 border-b border-white/10"
        >
          <div className={isElevate ? "lg:col-span-6" : "lg:col-span-7"}>
            <nav aria-label="Breadcrumb" data-testid="case-breadcrumb" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span aria-hidden>›</span>
              <Link to="/#work" className="hover:text-white transition-colors">Work</Link>
              <span aria-hidden>›</span>
              <span className="text-white/70 truncate max-w-[50vw]" aria-current="page">{project.title}</span>
            </nav>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">{project.category}</p>
            <h1
              data-testid="case-title"
              className="font-display font-extrabold text-6xl md:text-8xl lg:text-[7.5vw] tracking-tighter leading-[0.86]"
            >
              {project.title}
            </h1>
            <p className="text-white/60 text-lg leading-relaxed mt-8 max-w-xl">{project.summary}</p>
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-live-link"
                className="group inline-flex items-center gap-2 mt-6 text-sm text-white/80 hover:text-white transition-colors"
              >
                <span className="link-underline">{project.liveLabel || project.liveUrl}</span>
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            )}

            {/* Buoy on mobile (absolute panel is desktop-only) */}
            {isElevate && (
              <div className="lg:hidden w-full mt-10" style={{ height: "60vh" }}>
                <Buoy3D className="w-full h-full" />
              </div>
            )}

            {isElevate && <MetaGrid meta={meta} className="mt-12 max-w-md" />}
            {sideVisual && <MetaGrid meta={meta} className="mt-12 max-w-md" />}
          </div>

          {!isElevate && !sideVisual && (
            <div className="lg:col-span-5 lg:pl-8 flex flex-col justify-end gap-10">
              {project.slug === "chrome-intelligence-deck-template" && (
                <ChromeFace className="w-full max-w-[520px] ml-auto -mt-6 md:-mt-16 pointer-events-none" />
              )}
              <MetaGrid meta={meta} />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

// Seamless slide stack — Behance layout.
function SlideStack({ project }) {
  return (
    <section className="mt-14 md:mt-20" data-testid="slide-stack">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col gap-4 md:gap-6">
        {project.slides.map((slide, i) => {
          if (slide.type === "text") {
            return (
              <ScrollScale key={`text-${i}`} from={0.92} exit={0.6} className="w-full">
                <div className="py-6 md:py-12 max-w-3xl mx-auto text-center" data-testid={`slide-text-${i}`}>
                  <p className="font-display font-bold text-2xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white">
                    {slide.text}
                  </p>
                </div>
              </ScrollScale>
            );
          }
          if (slide.type === "quote") {
            return (
              <ScrollScale key={`quote-${i}`} from={0.94} exit={0.65} className="w-full">
                <figure className="py-12 md:py-24 max-w-4xl mx-auto text-center px-4" data-testid={`slide-quote-${i}`}>
                  <span className="font-serif-accent italic text-6xl md:text-7xl text-white/20 leading-none block mb-4">&ldquo;</span>
                  <blockquote className="font-display font-bold text-2xl md:text-4xl tracking-tight leading-[1.15] text-white">
                    {slide.text}
                  </blockquote>
                  <figcaption className="text-white/50 text-sm md:text-base mt-8">{slide.author}</figcaption>
                </figure>
              </ScrollScale>
            );
          }
          return (
            <ScrollScale key={`${slide.url}-${i}`} from={0.9} exit={0.55} className="w-full">
              <figure className="overflow-hidden" data-testid={`slide-${i}`}>
                {slide.type === "video" ? (
                  <DeckVideo slide={slide} i={i} />
                ) : (
                  <img
                    src={slide.url}
                    alt={slide.alt || `${project.title}, ${project.category} slide ${i + 1} designed by Kaya Amah`}
                    loading={i < 2 ? "eager" : "lazy"}
                    className="w-full block rounded-[24px] border-[1.5pt] border-white/30"
                  />
                )}
              </figure>
            </ScrollScale>
          );
        })}
      </div>
    </section>
  );
}

function CaseFooter({ project, next }) {
  const others = PROJECTS.filter((p) => p.slug !== project.slug);
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 mt-10 border-t border-white/10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
        <div>
          <p className="text-white/40 text-sm uppercase tracking-widest mb-3">Like what you see?</p>
          <Link
            to="/#contact"
            data-testid="case-cta"
            className="group inline-flex items-center gap-3 font-display font-bold text-3xl md:text-5xl tracking-tight hover:text-white/70 transition-colors"
          >
            Start a project <ArrowUpRight className="group-hover:rotate-45 transition-transform duration-300" size={36} />
          </Link>
        </div>
        <Link to={`/work/${next.slug}`} data-testid="case-next" className="group flex items-center gap-4 text-right">
          <div>
            <span className="text-white/40 text-xs uppercase tracking-widest block">Next project</span>
            <span className="font-display font-bold text-xl md:text-2xl">{next.title}</span>
          </div>
          <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
            <ArrowRight size={18} />
          </span>
        </Link>
      </div>

      <div className="mt-16 pt-10 border-t border-white/10 grid md:grid-cols-2 gap-10" data-testid="case-related-section">
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-4">More work</p>
          <ul className="flex flex-col gap-3">
            {others.map((p) => (
              <li key={p.slug}>
                <Link to={`/work/${p.slug}`} data-testid={`case-related-${p.slug}`} className="group inline-flex items-baseline gap-3 text-white/75 hover:text-white transition-colors">
                  <span className="font-display font-semibold text-lg">{p.title}</span>
                  <span className="text-xs uppercase tracking-widest text-white/40">{p.category}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-4">About the designer</p>
          <p className="text-white/55 leading-relaxed max-w-md">
            <Link to="/#about" className="link-underline text-white/80 hover:text-white">Kaya Amah</Link> is a Florida-based
            creative strategist designing keynotes and websites. See her{" "}
            <Link to="/#services" className="link-underline text-white/80 hover:text-white">services</Link>, read{" "}
            <Link to="/#faq" className="link-underline text-white/80 hover:text-white">common questions</Link> or view the
            full portfolio on{" "}
            <a href={BRAND.behance} target="_blank" rel="noopener noreferrer" data-testid="case-behance-link" className="link-underline text-white/80 hover:text-white">
              Behance
            </a>.
          </p>
        </div>
      </div>
    </section>
  );
}

const LEGACY_SLUGS = {
  planetfall: "future-of-cybersecurity-motion-keynote",
  "ai-commerce": "chrome-intelligence-deck-template",
  elevate: "unsinkable-cinematic-keynote-deck",
};

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const project = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  const legacyTarget = !project && LEGACY_SLUGS[slug];
  useEffect(() => {
    if (legacyTarget) navigate(`/work/${legacyTarget}`, { replace: true });
  }, [legacyTarget, navigate]);

  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const atTop = useAtTop();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const workSchema = useMemo(() => (project ? caseStudySchema(project) : null), [project]);

  if (!project) {
    if (legacyTarget) return null;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="case-not-found">
        <p className="text-white/60">Project not found.</p>
        <button onClick={() => navigate("/")} className="rounded-full border border-white/20 px-6 py-3">Back home</button>
      </div>
    );
  }

  const meta = [
    { label: "Role", value: project.role },
    { label: "Tools", value: (project.tools || []).join(" • ") },
  ];

  return (
    <main className="pt-28 md:pt-32" data-testid={`case-study-${project.slug}`}>
      <Seo
        title={project.seoTitle}
        description={project.seoDescription}
        image={project.cover.startsWith("/") ? `${SITE}${project.cover}` : project.cover}
        path={`/work/${project.slug}`}
        schema={workSchema}
      />
      <motion.div
        style={{ scaleX: progressX }}
        className="fixed top-0 left-0 right-0 h-[3px] bg-white/70 origin-left z-[60]"
        data-testid="case-progress"
      />
      <BackButton atTop={atTop} />
      <CaseIntro project={project} meta={meta} />
      {project.slug === "chrome-intelligence-deck-template" && (
        <ScrollScale from={0.9}>
          <TemplateIntro />
        </ScrollScale>
      )}
      <SlideStack project={project} />
      <CaseFooter project={project} next={next} />
    </main>
  );
}
